'use client';

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useHashDialog } from '@/hooks/use-hash-dialog';
import { SORTING_HASH } from '../constants';
import { sortingQueries } from '../services/sorting.queries';
import {
  useStartSortingMutation,
  useCompleteContainerMutation,
  useRefuseContainerMutation,
  useCompleteSortingMutation
} from '../services/sorting.mutations';
import { SortingContainerList } from './sorting-container-list';
import { SortingScanLog } from './sorting-scan-log';
import { SortingSummaryPanel } from './sorting-summary-panel';
import { RefuseContainerDialog } from './refuse-container-dialog';
import type { SortingSessionItem } from '../types';

interface SortingDetailPageProps {
  sessionId: number;
}

export function SortingDetailPage({ sessionId }: SortingDetailPageProps) {
  const t = useTranslations('sorting');
  const router = useRouter();

  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  // Refuse (move-to-repair) dialog open-state lives in the URL hash (#refuse)
  // so it is deep-linkable and closes on browser Back.
  const { open: refuseDialogOpen, onOpenChange: toggleRefuseDialog } =
    useHashDialog(SORTING_HASH.refuse);
  const autoClosedRef = useRef(false);

  const { data, isLoading } = useQuery(sortingQueries.detail(sessionId));
  const session = data?.data;

  const startMutation = useStartSortingMutation();
  const completeContainerMutation = useCompleteContainerMutation(sessionId);
  const refuseMutation = useRefuseContainerMutation(sessionId);
  const completeMutation = useCompleteSortingMutation();

  const isScanning = session?.status === 'in_progress';

  // Auto-start session when it loads in pending state
  useEffect(() => {
    if (session && session.status === 'pending') {
      startMutation.mutate(sessionId, {
        onError: (error: unknown) => {
          toast.error(error instanceof Error ? error.message : String(error));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  useEffect(() => {
    if (!session) return;
    if (session.status !== 'in_progress') return;
    if (autoClosedRef.current) return;
    if (completeMutation.isPending) return;

    const items = session.items ?? [];
    if (items.length === 0) return;
    const allTerminal = items.every(
      (i) => i.status === 'scanned' || i.status === 'refused'
    );
    if (!allTerminal) return;

    autoClosedRef.current = true;
    completeMutation.mutate(sessionId, {
      onSuccess: () => {
        toast.success(t('boxClosedAutomatically'));
        router.push('/dashboard/sorting');
      },
      onError: (error: unknown) => {
        autoClosedRef.current = false;
        toast.error(error instanceof Error ? error.message : String(error));
      }
    });
  }, [session, sessionId, completeMutation, router, t]);

  const handleAcceptContainer = () => {
    if (!selectedItem) return;
    completeContainerMutation.mutate(
      { item_id: selectedItem.id },
      {
        onSuccess: () => {
          toast.success(t('containerCompleted'));
          setSelectedItemId(null);
        },
        onError: (error: unknown) => {
          toast.error(error instanceof Error ? error.message : String(error));
        }
      }
    );
  };

  const handleMoveContainer = () => toggleRefuseDialog(true);

  const handleRefuseConfirm = (data: {
    registration_id: number;
    repair_destination_id: number;
    repair_reason_id: number;
    notes?: string;
  }) => {
    refuseMutation.mutate(data, {
      onSuccess: () => {
        toast.success(t('success'));
        toggleRefuseDialog(false);
        setSelectedItemId(null);
      },
      onError: (error: unknown) => {
        toast.error(error instanceof Error ? error.message : String(error));
      }
    });
  };

  const handleSelectItem = (item: SortingSessionItem) => {
    setSelectedItemId(item.id === selectedItemId ? null : item.id);
  };

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <Skeleton className='h-10 w-48' />
        <div className='grid gap-4 lg:grid-cols-[280px_1fr_280px]'>
          <Skeleton className='h-[500px]' />
          <Skeleton className='h-[500px]' />
          <Skeleton className='h-[500px]' />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className='flex flex-col items-center justify-center py-16'>
        <p className='text-muted-foreground'>{t('noSessions')}</p>
        <Button
          variant='outline'
          className='mt-4'
          onClick={() => router.push('/dashboard/sorting')}
        >
          <ArrowLeft className='me-2 h-4 w-4' />
          {t('pageTitle')}
        </Button>
      </div>
    );
  }

  const items = session.items ?? [];
  const selectedItem = items.find((i) => i.id === selectedItemId) ?? null;
  const activeContainer = items.find((i) => i.status === 'scanning') ?? null;

  return (
    <div className='flex flex-col gap-4 lg:h-[calc(100svh-6.5rem)]'>
      {/* Header */}
      <div className='flex shrink-0 items-center gap-4'>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => router.push('/dashboard/sorting')}
        >
          <ArrowLeft className='h-4 w-4' />
        </Button>
        <div>
          <h1 className='text-xl font-bold'>
            {session.box?.box_number ?? `#${session.box_id}`}
          </h1>
          <p className='text-muted-foreground text-sm'>{t('pageTitle')}</p>
        </div>
      </div>

      {/* Panel Layout */}
      <div className='grid gap-4 lg:min-h-0 lg:flex-1 lg:auto-rows-fr lg:grid-cols-[280px_1fr_280px]'>
        {/* Left: Summary Panel — order-3 on mobile (below Containers + Scan Log) */}
        <SortingSummaryPanel
          session={session}
          onMoveContainer={handleMoveContainer}
          selectedItemId={selectedItemId}
          className='order-3 lg:order-1'
        />

        {/* Center: Scan Log — order-2 on mobile */}
        <SortingScanLog
          items={items}
          isScanning={isScanning}
          activeContainer={activeContainer}
          selectedItem={selectedItem}
          onAcceptContainer={handleAcceptContainer}
          isAccepting={completeContainerMutation.isPending}
          className='order-2 lg:order-2'
        />

        {/* Right: Container List — order-1 on mobile (shown first) */}
        <SortingContainerList
          items={items}
          selectedItemId={selectedItemId}
          onSelectItem={handleSelectItem}
          totalContainers={session.total_containers}
          sortedContainers={session.sorted_containers}
          refusedContainers={session.refused_containers}
          className='order-1 lg:order-3'
        />
      </div>

      {/* Refuse Dialog — #refuse */}
      <RefuseContainerDialog
        open={refuseDialogOpen}
        onOpenChange={toggleRefuseDialog}
        item={selectedItem}
        onConfirm={handleRefuseConfirm}
        isPending={refuseMutation.isPending}
      />
    </div>
  );
}
